import { useState } from 'react';
import {
  Card, Badge, Button, AsyncBoundary, Drawer, useToast, Notice,
} from '@kollez/design-system';
import { useFlaggedOpportunities, useClearFraudFlag, useRerunFraudCheck, type FlaggedOpportunity } from '@/lib/queries';

/**
 * /platform/fraud (F70) — Nexie flagged opportunities queue. Platform_operator or
 * platform_admin reviews each and clears with a reason (or leaves flagged, in which case the
 * recruiter cannot publish).
 */
export function PlatformFraudQueuePage() {
  const list = useFlaggedOpportunities();
  const [selected, setSelected] = useState<FlaggedOpportunity | null>(null);

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Fraud queue</p>
          <h1 className="h-page">Opportunities Nexie flagged</h1>
          <p className="subhead">Review each. Clearing the flag lets the recruiter publish; leaving it blocks publish.</p>
        </div>
      </div>

      <AsyncBoundary kind="table" loading={list.isLoading} error={list.error}
        empty={!list.isLoading && !list.data?.flagged.length}
        emptyTitle="Nothing flagged"
        emptyHint="Nexie is watching every new opening — flags land here when it sees fee-charging language, unverified employers, or salary anomalies."
        onRetry={() => void list.refetch()}
      >
        <Card padding="md">
          <ul className="grid gap-2">
            {list.data?.flagged.map((o) => (
              <li key={o._id} className="p-3 rounded border border-line-primary">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium">{o.title}</div>
                    <div className="text-xs text-ink-tertiary">
                      {o.partner?.name ?? 'Unknown partner'}
                      {o.partner?.verifiedAt ? '' : ' · unverified'}
                      · {new Date(o.createdAt).toLocaleDateString('en-IN')}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone={o.fraudCheck.riskScore >= 70 ? 'warning' : 'info'}>
                      risk {o.fraudCheck.riskScore}
                    </Badge>
                    <Badge tone="outline">{o.fraudCheck.signals.length} signal(s)</Badge>
                    <Button variant="ghost" onClick={() => setSelected(o)}>Review</Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </AsyncBoundary>

      {selected && <FraudReviewDrawer opp={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function FraudReviewDrawer({ opp, onClose }: { opp: FlaggedOpportunity; onClose: () => void }) {
  const clear = useClearFraudFlag();
  const rerun = useRerunFraudCheck();
  const toast = useToast();
  const [reason, setReason] = useState('');

  async function doRerun() {
    try {
      const res = await rerun.mutateAsync(opp._id);
      toast.show({
        message: res.fraudCheck.shouldFlag
          ? `Still flagged — risk ${res.fraudCheck.riskScore} across ${res.fraudCheck.signals.length} signals.`
          : `Cleared by re-run — risk ${res.fraudCheck.riskScore}, no signals tripped.`,
      });
    } catch (err) {
      toast.show({ tone: 'warning', message: err instanceof Error ? err.message : 'Rerun failed.' });
    }
  }

  async function submit() {
    if (reason.trim().length < 10) { toast.show({ tone: 'warning', message: 'Reason must be at least 10 characters.' }); return; }
    try {
      await clear.mutateAsync({ id: opp._id, reason });
      toast.show({ message: 'Flag cleared — recruiter can now publish.' });
      onClose();
    } catch {
      toast.show({ tone: 'warning', message: 'Could not clear flag.' });
    }
  }

  return (
    <Drawer open onClose={onClose} title={opp.title} size="lg">
      <div className="stack">
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-tertiary">Risk score</div>
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-4xl">{opp.fraudCheck.riskScore}</span>
            <span className="text-ink-tertiary">/100</span>
          </div>
        </div>

        <div>
          <h3 className="h-subsection mb-2">Signals</h3>
          <ul className="grid gap-2">
            {opp.fraudCheck.signals.map((s, i) => (
              <li key={i} className="p-2 rounded border border-line-primary">
                <div className="flex items-baseline gap-2 mb-1">
                  <Badge tone={s.severity === 'high' ? 'warning' : s.severity === 'medium' ? 'info' : 'outline'}>
                    {s.severity}
                  </Badge>
                  <span className="text-xs text-ink-tertiary">{s.code.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-sm">{s.message}</div>
                {s.evidence && (
                  <pre className="mt-1 text-xs bg-bg-secondary/40 p-2 rounded overflow-x-auto">{s.evidence}</pre>
                )}
              </li>
            ))}
          </ul>
        </div>

        <Notice tone="info">
          Clear only when you've verified the employer or confirmed the language is legitimate. Every clear writes to the audit log.
        </Notice>

        <div>
          <label className="text-xs uppercase tracking-wide text-ink-tertiary">Reason for clearing (min 10 chars)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="input w-full mt-1"
            placeholder="Called the recruiter, confirmed the fee is a refundable relocation deposit paid by the company, not the candidate."
          />
        </div>

        <div className="flex justify-end gap-2 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => void doRerun()}
            loading={rerun.isPending}
            title="Re-evaluate the fraud signals against current opportunity + partner state"
          >
            Re-run detection
          </Button>
          <Button variant="ghost" onClick={onClose}>Close (leave flagged)</Button>
          <Button variant="primary" onClick={submit} loading={clear.isPending}>Clear flag</Button>
        </div>
      </div>
    </Drawer>
  );
}
